/**
 * Appointment Management Hooks
 *
 * TanStack Query hooks for appointment operations
 * Optimized for caching with intelligent refresh intervals
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { reviewRequestKeys } from '../reviewRequest/reviewRequest.hooks.js';
import {
    addServiceToAppointmentApi,
    cancelAppointmentApi,
    createAppointmentApi,
    deleteServiceFromAppointmentApi,
    getAppointmentDetailsApi,
    getAppointmentListApi,
    getAppointmentRequestCardCountApi,
    getAppointmentRequestListApi,
    getKanbanBoardListApi,
    getMechanicDetailsApi,
    proposeDateTimeApi,
    rescheduleAcceptDeclineApi,
    rescheduleAppointmentApi,
    updateAppointmentNoteApi,
    updateAppointmentStatusApi,
} from './appointment.api';

// Query Keys for appointment domain
export const appointmentKeys = {
    all: ['appointment'],
    kanbanBoard: (timeZone) => [...appointmentKeys.all, 'kanban-board', timeZone],
    details: (id) => [...appointmentKeys.all, 'details', id],
    list: (params) => [...appointmentKeys.all, 'list', params],
    infiniteList: (params) => [...appointmentKeys.all, 'infiniteList', params],
    mechanicDetails: () => [...appointmentKeys.all, 'mechanic-details'],
    requests: (params) => [...appointmentKeys.all, 'requests', params],
    requestsCard: () => [...appointmentKeys.all, 'requests', 'statistics'],
};

/**
 * Hook to get kanban board list
 *
 * Uses optimized caching for appointment kanban board data:
 * - 5-minute stale time for efficient caching
 * - Automatic background refetching when data becomes stale
 * - Smart retry logic for failed requests
 *
 * @param {string} timeZone - The timezone for appointment filtering
 * @param {object} options - Query options
 * @returns {object} Query result with kanban board data
 */
export const useKanbanBoardList = (timeZone, options = {}) => {
    return useQuery({
        queryKey: appointmentKeys.kanbanBoard(timeZone),
        queryFn: () => getKanbanBoardListApi(timeZone),
        staleTime: 0, // 5 minutes
        gcTime: 0, // 10 minutes
        enabled: !!timeZone,
        fetchOnWindowFocus: true,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        ...options,
    });
};

/**
 * Hook to get appointment details
 *
 * Uses optimized caching for appointment details:
 * - 10-minute stale time for efficient caching
 * - Automatic background refetching when data becomes stale
 * - Smart retry logic for failed requests
 *
 * @param {string} id - The appointment ID
 * @param {object} options - Query options
 * @returns {object} Query result with appointment details
 */
export const useAppointmentDetails = (id, options = {}) => {
    return useQuery({
        queryKey: appointmentKeys.details(id),
        queryFn: () => getAppointmentDetailsApi(id),
        enabled: !!id,
        ...options,
    });
};

/**
 * Hook to get appointment list with filters
 *
 * Uses optimized caching for appointment lists:
 * - 5-minute stale time for efficient caching
 * - Automatic background refetching when data becomes stale
 * - Smart retry logic for failed requests
 *
 * @param {object} params - Query parameters for filtering
 * @param {object} options - Query options
 * @returns {object} Query result with appointment list
 */
export const useAppointmentList = (params, options = {}) => {
    return useQuery({
        queryKey: appointmentKeys.list(params),
        queryFn: () => getAppointmentListApi(params),
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        ...options,
    });
};

/**
 * Hook to get infinite appointment list with search and pagination
 *
 * Uses infinite query for optimized loading and caching:
 * - Infinite scroll pagination
 * - Smart search debouncing
 * - Optimized caching with search parameters
 * - Background refetch for better UX
 * - Automatic cache invalidation on appointment changes
 *
 * @param {object} params - Query parameters
 * @param {number} params.limit - Number of items per page (default: 20)
 * @param {string} params.customerId - Customer ID filter
 * @param {string} params.customerSearch - Customer search term
 * @param {object} options - Query options
 * @returns {object} Infinite query result with appointment data
 */
export const useInfiniteAppointmentList = (params = {}, options = {}) => {
    // Set default limit if not provided
    const queryParams = {
        limit: 20,
        ...params,
    };

    return useInfiniteQuery({
        queryKey: appointmentKeys.infiniteList(queryParams),
        queryFn: ({ pageParam = 1 }) => {
            return getAppointmentListApi({
                ...queryParams,
                page: pageParam,
            });
        },
        getNextPageParam: (lastPage, allPages) => {
            // Handle different API response structures
            let lastPageData = [];
            let hasMore = false;

            // If lastPage is an array directly (most likely case based on current usage)
            if (Array.isArray(lastPage)) {
                lastPageData = lastPage;
                // If the last page has items equal to the limit, there might be more pages
                hasMore = lastPage.length === queryParams.limit;
            }
            // If API returns an object with data array and pagination info
            else if (lastPage && typeof lastPage === 'object') {
                // Check for pagination metadata
                if (lastPage.totalPages !== undefined) {
                    hasMore = allPages.length < lastPage.totalPages;
                } else if (lastPage.hasMore !== undefined) {
                    hasMore = lastPage.hasMore;
                } else if (lastPage.data && Array.isArray(lastPage.data)) {
                    lastPageData = lastPage.data;
                    hasMore = lastPageData.length === queryParams.limit;
                } else if (Array.isArray(lastPage.appointments)) {
                    lastPageData = lastPage.appointments;
                    hasMore = lastPageData.length === queryParams.limit;
                }
            }

            return hasMore ? allPages.length + 1 : undefined;
        },
        getPreviousPageParam: (firstPage, allPages) => {
            // For previous page support (optional)
            if (allPages.length > 1) {
                return allPages.length - 1;
            }
            return undefined;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        refetchOnWindowFocus: false, // Prevent refetch on window focus for better performance
        ...options,
    });
};

/**
 * Hook to get mechanic details
 *
 * Uses optimized caching for mechanic details:
 * - 15-minute stale time for efficient caching
 * - Automatic background refetching when data becomes stale
 * - Smart retry logic for failed requests
 *
 * @param {object} options - Query options
 * @returns {object} Query result with mechanic details
 */
export const useMechanicDetails = (options = {}) => {
    return useQuery({
        queryKey: appointmentKeys.mechanicDetails(),
        queryFn: getMechanicDetailsApi,
        staleTime: 15 * 60 * 1000, // 15 minutes
        gcTime: 30 * 60 * 1000, // 30 minutes
        ...options,
    });
};

/**
 * Hook to get appointment requests list
 *
 * Ensures fresh data on every page mount while still leveraging
 * React Query caching during the session.
 *
 * @param {object} params - Query parameters for filtering
 * @param {object} options - Query options
 * @returns {object} Query result with appointment requests list
 */
export const useAppointmentRequests = (params, options = {}) => {
    return useQuery({
        queryKey: appointmentKeys.requests(params),
        queryFn: () => getAppointmentRequestListApi(params),

        // Always fetch fresh data when component mounts
        staleTime: 0,
        gcTime: 5 * 60 * 1000,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,

        ...options,
    });
};

/**
 * Hook to get appointment request card counts
 *
 * Ensures latest counts are fetched whenever user lands on the page.
 *
 * @param {object} params - Optional query params
 * @param {object} options - Query options
 * @returns {object} Query result with request counts
 */
export const useAppointmentRequestsCard = (options = {}) => {
    return useQuery({
        queryKey: appointmentKeys.requestsCard(),
        queryFn: () => getAppointmentRequestCardCountApi(), // replace if separate API exists

        // Always fetch fresh data on mount
        staleTime: 0,
        gcTime: 5 * 60 * 1000,
        refetchOnMount: 'always',
        refetchOnWindowFocus: false,

        ...options,
    });
};

/**
 * Hook to update appointment status
 *
 * Uses optimistic updates and cache invalidation:
 * - Automatically invalidates related queries
 * - Optimistic updates for better UX
 * - Smart retry logic for failed requests
 * - Note: Optimistic updates are handled in components using setQueryData
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result
 */
export const useUpdateAppointmentStatus = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }) => updateAppointmentStatusApi(id, payload),
        onSuccess: (data, variables) => {
            // Don't invalidate kanban board immediately since optimistic updates handle it
            // Only invalidate appointment details to ensure they're fresh
            if (variables.id) {
                queryClient.invalidateQueries({
                    queryKey: appointmentKeys.details(variables.id),
                });
            }
            // Appointment status changes affect "last visit" — keep Quick Review users fresh
            queryClient.invalidateQueries({ queryKey: [...reviewRequestKeys.all, 'quickReviewUsers'] });
        },
        onError: (error, variables, context) => {
            // On error, invalidate kanban board to revert optimistic updates
            queryClient.invalidateQueries({
                queryKey: appointmentKeys.all,
            });
        },
        ...options,
    });
};

/**
 * Hook to create appointment
 *
 * Uses cache invalidation for related queries:
 * - Automatically invalidates appointment lists
 * - Optimistic updates for better UX
 * - Smart retry logic for failed requests
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result
 */
export const useCreateAppointment = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createAppointmentApi,
        onSuccess: () => {
            // Invalidate all appointment queries
            queryClient.invalidateQueries({
                queryKey: appointmentKeys.all,
            });
            // New appointment should refresh Quick Review users list everywhere
            queryClient.invalidateQueries({ queryKey: [...reviewRequestKeys.all, 'quickReviewUsers'] });
        },
        ...options,
    });
};

/**
 * Hook to reschedule appointment
 *
 * Uses cache invalidation for related queries:
 * - Automatically invalidates appointment details and lists
 * - Optimistic updates for better UX
 * - Smart retry logic for failed requests
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result
 */
export const useRescheduleAppointment = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }) => rescheduleAppointmentApi(id, payload),
        onSuccess: (data, variables) => {
            // Invalidate all appointment queries
            queryClient.invalidateQueries({
                queryKey: appointmentKeys.all,
            });

            // Invalidate specific appointment details
            if (variables.id) {
                queryClient.invalidateQueries({
                    queryKey: appointmentKeys.details(variables.id),
                });
            }
            // Reschedule changes "last visit" — keep Quick Review users fresh
            queryClient.invalidateQueries({ queryKey: [...reviewRequestKeys.all, 'quickReviewUsers'] });
        },
        ...options,
    });
};

/**
 * Hook to cancel appointment
 *
 * Uses cache invalidation for related queries:
 * - Automatically invalidates appointment lists
 * - Optimistic updates for better UX
 * - Smart retry logic for failed requests
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result
 */
export const useCancelAppointment = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }) => cancelAppointmentApi(id, payload),
        onSuccess: (data, variables) => {
            // Invalidate all appointment queries
            queryClient.invalidateQueries({
                queryKey: appointmentKeys.all,
            });

            // Invalidate specific appointment details
            if (variables.id) {
                queryClient.invalidateQueries({
                    queryKey: appointmentKeys.details(variables.id),
                });
            }
        },
        ...options,
    });
};

/**
 * Hook to propose new date and time
 *
 * Uses cache invalidation for related queries:
 * - Automatically invalidates appointment details
 * - Optimistic updates for better UX
 * - Smart retry logic for failed requests
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result
 */
export const useProposeDateTime = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }) => proposeDateTimeApi(id, payload),
        onSuccess: (data, variables) => {
            // Invalidate appointment details
            if (variables.id) {
                queryClient.invalidateQueries({
                    queryKey: appointmentKeys.details(variables.id),
                });
            }
        },
        ...options,
    });
};

/**
 * Hook to add service to appointment
 *
 * Uses cache invalidation for related queries:
 * - Automatically invalidates appointment details
 * - Optimistic updates for better UX
 * - Smart retry logic for failed requests
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result
 */
export const useAddServiceToAppointment = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }) => addServiceToAppointmentApi(id, payload),
        onSuccess: (data, variables) => {
            // Invalidate appointment details and kanban board
            queryClient.invalidateQueries({
                queryKey: appointmentKeys.all,
            });
        },
        ...options,
    });
};

/**
 * Hook to delete service from appointment
 *
 * Uses cache invalidation for related queries:
 * - Automatically invalidates appointment details
 * - Optimistic updates for better UX
 * - Smart retry logic for failed requests
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result
 */
export const useDeleteServiceFromAppointment = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, serviceId }) => deleteServiceFromAppointmentApi(id, serviceId),
        onSuccess: (data, variables) => {
            // Invalidate appointment details and kanban board
            queryClient.invalidateQueries({
                queryKey: appointmentKeys.all,
            });
        },
        ...options,
    });
};

/**
 * Hook to update appointment note
 *
 * Uses cache invalidation for related queries:
 * - Automatically invalidates appointment details
 * - Optimistic updates for better UX
 * - Smart retry logic for failed requests
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result
 */
export const useUpdateAppointmentNote = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }) => updateAppointmentNoteApi(id, payload),
        onSuccess: (data, variables) => {
            // Invalidate appointment details
            if (variables.id) {
                queryClient.invalidateQueries({
                    queryKey: appointmentKeys.details(variables.id),
                });
            }
        },
        ...options,
    });
};

/**
 * Hook to accept or decline reschedule request
 *
 * Uses cache invalidation for related queries:
 * - Automatically invalidates appointment details and lists
 * - Optimistic updates for better UX
 * - Smart retry logic for failed requests
 *
 * @param {object} options - Mutation options
 * @returns {object} Mutation result
 */
export const useRescheduleAcceptDecline = (options = {}) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, payload }) => rescheduleAcceptDeclineApi(id, payload),
        onSuccess: (data, variables) => {
            // Invalidate all appointment queries
            queryClient.invalidateQueries({
                queryKey: appointmentKeys.all,
            });

            // Invalidate specific appointment details
            if (variables.id) {
                queryClient.invalidateQueries({
                    queryKey: appointmentKeys.details(variables.id),
                });
            }
        },
        ...options,
    });
};
